"use client";

import React, { useState } from "react";
import FadeIn from "../ui/FadeIn";

const faqs = [
  {
    question: "What is AdviSify?",
    answer:
      "AdviSify connects you to verified Local Advisors who offer practical, location-specific recommendations in real time.",
  },
  {
    question: "Can I use AdviSify before my trip and during my trip?",
    answer:
      "Yes. You can plan with your advisor before you leave and continue getting guidance while you're on the ground.",
  },
  {
    question: "Is this AI or real people?",
    answer:
      "Real people. Advisors are verified locals with first-hand city knowledge.",
  },
  {
    question: "What kind of questions can I ask?",
    answer:
      "Anything related to your trip: places to visit, food, neighborhoods, logistics, timing, safety and more.",
  },
  {
    question: "Can I ask just one question or many?",
    answer: "Both. Ask a single question or get full trip guidance end to end.",
  },
  {
    question: "Do I need to plan everything in advance?",
    answer:
      "No. Advisors can help you prepare early or adjust in real time while traveling.",
  },
  {
    question: "Who makes the recommendations?",
    answer:
      "Recommendations come directly from your local advisor based on your preferences and context.",
  },
  {
    question: "Do I choose the places, or do you decide for me?",
    answer:
      "You choose the level of control. Advisors recommend, and you decide what fits.",
  },
  {
    question: "Can AdviSify make reservations for me?",
    answer:
      "Advisors can guide where and when to book. Reservation handling depends on the service and location.",
  },
  {
    question: "How do payments work?",
    answer:
      "Payments are handled securely in-app with clear pricing before confirmation.",
  },
  {
    question: "How does this save me time?",
    answer:
      "You avoid scattered research and trial-and-error by getting relevant local guidance quickly.",
  },
  {
    question: "How do you avoid bad recommendations?",
    answer:
      "Advisor vetting, traveler feedback loops, and context-based matching help maintain quality.",
  },
  {
    question: "What if something goes wrong during my trip?",
    answer:
      "You can reach out for alternatives and contingency suggestions based on your location and timing.",
  },
  {
    question: "Can you help with serious issues, like finding a doctor?",
    answer:
      "Yes. Advisors can guide you to trusted local options and urgent resources where possible.",
  },
  {
    question: "Why is this better than Google or travel apps?",
    answer:
      "Because you get contextual human advice, not generic lists designed for everyone.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="px-4 py-12 md:py-14">
      <div className="mx-auto max-w-6xl rounded-[16px] bg-[#F6F7F9] px-5 py-8 md:px-10 md:py-10">
        <div className="grid gap-8 md:grid-cols-12 md:gap-6">
          <div className="md:col-span-4 md:pr-6">
            <FadeIn direction="right">
              <h2 className="text-4xl font-medium leading-[1.2] tracking-tight text-[#0A2B1C] md:sticky md:top-24 md:text-[42px]">
                Frequently asked <br className="hidden md:block" />
                Questions!
              </h2>
            </FadeIn>
          </div>

          <div className="md:col-span-8">
            <div className="flex flex-col gap-4">
              {faqs.map((faq, index) => {
                const isOpen = openIndex === index;
                return (
                  <FadeIn
                    key={index}
                    direction="up"
                    delay={0.1 * index}
                    className="border-b border-[#D7DDE2] pb-3 last:border-0 last:pb-0 group"
                  >
                    <div
                      onMouseEnter={() => setOpenIndex(index)}
                      className="cursor-pointer"
                    >
                      <button
                        onClick={() => toggleFAQ(index)}
                        className="flex w-full items-center justify-between py-2 text-left focus:outline-hidden"
                      >
                        <span className="pr-4 text-[15px] font-medium text-[#0A2B1C] md:text-[18px] transition-colors duration-300 group-hover:text-[#51CBBE]">
                          {faq.question}
                        </span>
                        <span className="ml-4 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#0A2B1C] transition-transform duration-300 group-hover:scale-110">
                          {isOpen ? (
                            <svg
                              width="14"
                              height="2"
                              viewBox="0 0 14 2"
                              fill="currentColor"
                            >
                              <rect width="14" height="2" />
                            </svg>
                          ) : (
                            <svg
                              className="group-hover:text-[#51CBBE]"
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="currentColor"
                            >
                              <path d="M8 0H6V6H0V8H6V14H8V8H14V6H8V0Z" />
                            </svg>
                          )}
                        </span>
                      </button>
                      <div
                        className={`overflow-hidden text-left transition-all duration-500 ease-in-out ${
                          isOpen
                            ? "mt-2 max-h-44 opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <p className="text-[13px] leading-6 text-[#403F3C]">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
